import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TblFltOption } from './table.types';
import { useClickOutside } from '../hooks/useClickOutside';
import { MultiSelectOption } from '../components/validation/ValidInputMultiSelect';
import { IconEmptySquare } from '../components/Icon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

type TblFltInputMultiSelectProps = {
    id: string;
    val: string[];
    options: TblFltOption[];
    onFilter: (val: (string | number)[]) => void;
    align?: 'left' | 'right';
};

const TableFltInputMultiSelect = ({
    id,
    val,
    options,
    onFilter,
    align = 'left',
}: TblFltInputMultiSelectProps): JSX.Element => {
    const [expanded, setExpanded] = useState(false);
    const [filterTerm, setFilterTerm] = useState<string>('');

    // No effect: do not fire onFilter on mount/options change. Visual selection handled below.

    const filteredOptions = useMemo(() => {
        if (!filterTerm) return options;
        const term = filterTerm.toLowerCase();
        return options.filter(opt => opt.lbl.toLowerCase().includes(term));
    }, [options, filterTerm]);
    const domNode = useRef<HTMLDivElement>(null);

    // Track if user has ever cleared all (to distinguish between default and explicit clear)
    const userClearedAllRef = useRef(false);

    useEffect(() => {
        if (val.length === 0 && userClearedAllRef.current) {
            // Already marked as cleared
            return;
        }
        if (val.length === 0 && options.length > 0) {
            // If not marked as cleared, treat as all selected for display
            userClearedAllRef.current = false;
        }
    }, [val, options.length]);

    useClickOutside(domNode, () => setExpanded(false));

    const allSelected =
        options.length > 0 && ((val.length === 0 && !userClearedAllRef.current) || val.length === options.length);

    const selectedOptions: string[] = useMemo(() => {
        if (options.length === 0) return [];
        if (allSelected) {
            return options.map(opt => String(opt.val));
        }
        // If val is empty and user has cleared all, treat as none selected
        if (val.length === 0 && userClearedAllRef.current) return [];
        return val.map(v => String(v));
    }, [options, val, allSelected]);

    const totalSelected = val.length;

    const placeholderText = useMemo(() => {
        if (totalSelected === 1) {
            return val[0];
        }
        if (totalSelected === 0 && userClearedAllRef.current) {
            return 'None';
        }
        if (totalSelected === 0 || totalSelected === options.length) {
            return 'All';
        }
        return `${totalSelected} selected`;
    }, [totalSelected, val, options.length]);

    const handleSelectOrDeselectAll = useCallback(
        (event): void => {
            event.stopPropagation();

            if (allSelected) {
                userClearedAllRef.current = true;
                onFilter([]);
            } else {
                userClearedAllRef.current = false;
                const opts = options.map(opt => opt.val);
                onFilter(opts);
            }
        },
        [allSelected, onFilter, options],
    );

    const handleSelectOrDeselectOption = useCallback(
        (val: string | number) => {
            const selected = selectedOptions.includes(val as any);
            let nextSelectedOptions;
            if (selected) {
                // Deselecting an option: remove it from selection
                nextSelectedOptions = selectedOptions.filter(opt => opt !== val && opt !== 'ALL' && opt !== 'All');
            } else {
                // Selecting an option: add it to selection, but do not add 'ALL'
                nextSelectedOptions = [...selectedOptions.filter(opt => opt !== 'ALL' && opt !== 'All'), val];
            }
            onFilter(nextSelectedOptions);
        },
        [onFilter, selectedOptions],
    );

    const handleToggle = useCallback((): void => {
        setExpanded(curr => !curr);
    }, []);

    const handleFilterList = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        setFilterTerm(event.target.value.toLowerCase());
    }, []);

    return (
        <div
            id={id}
            ref={domNode}
            className="drop-select drop-select-v2 drop-row1 inline-filter"
        >
            <p
                className="drop-select-label"
                onClick={handleToggle}
            >
                <span>{placeholderText}</span>
                <button
                    type="button"
                    className="btn-alt"
                >
                    <span className="icon-glyph glyphicon-filter" />
                </button>
            </p>
            <div
                className={`drop-select-list-v2 ${expanded ? 'show-list' : ''}`}
                style={align === 'right' ? { right: 0, left: 'auto' } : undefined}
            >
                <ul>
                    <li className="multiselect-search">
                        <input
                            type="text"
                            value={filterTerm}
                            onChange={handleFilterList}
                            placeholder="Filter options..."
                        />
                    </li>
                    <li
                        onClick={handleSelectOrDeselectAll}
                        className={`multiselect-selected-option${allSelected ? ' multiselect-option-selected' : ''}`}
                    >
                        {allSelected ? (
                            <FontAwesomeIcon
                                icon={faCheck}
                                color="#2BB842"
                                style={{ strokeWidth: 30, stroke: '#2BB842', minWidth: 16 }}
                            />
                        ) : (
                            <IconEmptySquare size={14} />
                        )}
                        <p>All</p>
                    </li>
                    {filteredOptions.map((opt, index) => {
                        // Use selectedOptions for checkmark logic
                        const selected = selectedOptions.includes(String(opt.val));
                        return (
                            <MultiSelectOption
                                key={`${opt.lbl}-${index}`}
                                opt={opt}
                                selected={selected}
                                onClick={handleSelectOrDeselectOption}
                            />
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

export default TableFltInputMultiSelect;
